
import React from 'react';
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Crown, Zap } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";

interface CopilotUsageMeterProps {
  usedCount: number;
  limit: number;
  percentUsed: number;
  canSend: boolean;
  subscriptionTier?: string;
}

const CopilotUsageMeter: React.FC<CopilotUsageMeterProps> = ({ 
  usedCount, 
  limit, 
  percentUsed,
  canSend,
  subscriptionTier = 'free'
}) => {
  const navigate = useNavigate();
  const isNearLimit = percentUsed > 80 && percentUsed <= 100;
  const isOverLimit = percentUsed >= 100;
  
  const getTierInfo = () => {
    switch (subscriptionTier.toLowerCase()) {
      case 'plus':
        return { name: 'Plus', color: 'text-blue-600', icon: <Zap className="h-3 w-3" /> };
      case 'pro':
        return { name: 'Pro', color: 'text-purple-600', icon: <Crown className="h-3 w-3" /> };
      default:
        return { name: 'Free', color: 'text-gray-600', icon: null };
    }
  };

  const tierInfo = getTierInfo();
  
  return (
    <div className="p-3 border-b border-border bg-muted/20">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={cn("text-xs", tierInfo.color)}>
            {tierInfo.icon}
            <span className="ml-1">{tierInfo.name} Plan</span>
          </Badge>
        </div>
        
        {isOverLimit && (
          <Button 
            variant="default" 
            size="sm" 
            onClick={() => navigate('/pricing')}
            className="text-xs h-6"
          >
            Upgrade
          </Button>
        )}
      </div>
      
      <div className="space-y-2">
        <div className="flex justify-between items-center text-xs">
          <span className="font-medium">
            Product Copilot: {usedCount}/{limit === 999999 ? '∞' : limit}
            {isOverLimit && (
              <span className="text-red-500 ml-1">(Limit reached)</span>
            )}
          </span>
          
          <span className={cn(
            "text-xs",
            isOverLimit ? "text-red-500" : 
            isNearLimit ? "text-amber-500" : 
            "text-muted-foreground"
          )}>
            {percentUsed.toFixed(0)}% used
          </span>
        </div>
        
        <Progress 
          value={Math.min(percentUsed, 100)} 
          className={cn(
            "h-1.5",
            "[&>div]:transition-all",
            isOverLimit && "[&>div]:bg-red-500",
            isNearLimit && "[&>div]:bg-amber-500",
            !isNearLimit && !isOverLimit && "[&>div]:bg-primary"
          )}
        />
        
        {!canSend && (
          <div className="text-xs text-red-600 bg-red-50 dark:bg-red-900/20 p-2 rounded">
            <div className="font-medium">Message limit reached</div>
            <div>Upgrade to continue using Product Copilot</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CopilotUsageMeter;
